import React from 'react';
import { View, Text, TouchableOpacity, StyleSheet } from 'react-native';
import { PRIMITIVE_COLORS, SEMANTIC_COLORS } from '@/constants/residents/residentColors';
import { RESIDENTS_SVG_COMPONENTS } from '@/assets/residents';

interface DesktopBulletinTileProps {
  unreadCount: number;
  onPress: () => void;
}

/**
 * DesktopBulletinTile - Bulletin notifications tile for desktop home dashboard
 */
export const DesktopBulletinTile: React.FC<DesktopBulletinTileProps> = ({
  unreadCount,
  onPress,
}) => {
  const hasUnread = unreadCount > 0;

  const getStatusText = (): string => {
    if (unreadCount === 0) return 'No new bulletins';
    if (unreadCount === 1) return '1 unread bulletin';
    return `${unreadCount} unread bulletins`;
  };

  return (
    <TouchableOpacity style={styles.tile} onPress={onPress} activeOpacity={0.8}>
      <View style={styles.header}>
        <View style={[styles.iconContainer, hasUnread && styles.iconContainerActive]}>
          <RESIDENTS_SVG_COMPONENTS.BELL 
            width={24} 
            height={24} 
            color={hasUnread ? PRIMITIVE_COLORS.warningOrange : PRIMITIVE_COLORS.resioBlue} 
          />
        </View>
        <Text style={styles.title}>Bulletins</Text>
        {hasUnread && (
          <View style={styles.badge}>
            <Text style={styles.badgeText}>{unreadCount}</Text>
          </View>
        )}
      </View>

      <View style={styles.content}>
        <Text style={styles.description}>
          Stay updated with important announcements from your property management.
        </Text>
        <Text style={styles.status}>{getStatusText()}</Text>
      </View>

      <TouchableOpacity
        style={styles.actionButton}
        onPress={(e) => {
          e.stopPropagation();
          onPress();
        }}
        activeOpacity={0.8}
      >
        <Text style={styles.actionButtonText}>View bulletins</Text>
      </TouchableOpacity>
    </TouchableOpacity>
  );
};

const styles = StyleSheet.create({
  tile: {
    backgroundColor: PRIMITIVE_COLORS.gray900,
    borderRadius: 16,
    padding: 20,
    flex: 1,
    minHeight: 160,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    marginBottom: 16,
  },
  iconContainer: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: PRIMITIVE_COLORS.white10,
    justifyContent: 'center',
    alignItems: 'center',
  },
  iconContainerActive: {
    backgroundColor: 'rgba(255, 118, 41, 0.15)',
  },
  title: {
    fontSize: 16,
    fontWeight: '600',
    color: SEMANTIC_COLORS.text.primary,
    flex: 1,
  },
  badge: {
    backgroundColor: PRIMITIVE_COLORS.warningOrange,
    borderRadius: 12,
    paddingHorizontal: 8,
    paddingVertical: 2,
    minWidth: 24,
    alignItems: 'center',
  },
  badgeText: {
    fontSize: 12,
    fontWeight: '700',
    color: PRIMITIVE_COLORS.gray950,
  },
  content: {
    flex: 1,
  },
  description: {
    fontSize: 14,
    color: SEMANTIC_COLORS.text.secondary,
    lineHeight: 20,
    marginBottom: 8,
  },
  status: {
    fontSize: 14,
    color: SEMANTIC_COLORS.text.tertiary,
  },
  actionButton: {
    backgroundColor: PRIMITIVE_COLORS.resioBlue,
    borderRadius: 8,
    paddingVertical: 10,
    paddingHorizontal: 16,
    alignSelf: 'flex-start',
    marginTop: 16,
  },
  actionButtonText: {
    fontSize: 13,
    fontWeight: '600',
    color: SEMANTIC_COLORS.text.primary,
  },
});

export default DesktopBulletinTile;
